/**
 * PAY-4392: one celebration, as a seeded scene the canvas draws.
 *
 * Every style is a construction scene (PAY-4631, PAY-4644): props drawn on
 * the canvas and a pose for the notice, both a function of time alone. A
 * scene is deterministic for a seed, a viewport and the notice's resting box,
 * so tests can build one without a canvas. The renderer
 * (`celebration-burst.tsx`) is the only thing that draws it.
 */

import {
  type CelebrationPaletteName,
  type CelebrationStyle,
  CELEBRATION_DURATION_S,
  CELEBRATION_PALETTES,
  createRng,
} from './celebration-variety';
import type { StageGeometry } from './construction-kit';
import {
  type CardPose,
  type ConstructionScene,
  buildConstructionScene,
  constructionCardPose,
} from './construction-scene';

export { CELEBRATION_DURATION_S } from './celebration-variety';
export type { CardPose } from './construction-scene';

/** The notice's resting box in the viewport, in CSS pixels. */
export interface CardRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CelebrationScene {
  style: CelebrationStyle;
  paletteName: CelebrationPaletteName;
  seed: number;
  width: number;
  height: number;
  stage: StageGeometry;
  construction: ConstructionScene;
}

/** The notice's usual box: 26rem wide, centred. For callers that cannot measure it. */
function defaultCardRect(width: number, height: number): CardRect {
  const cardWidth = Math.min(416, width - 48);
  const cardHeight = 240;
  return {
    left: (width - cardWidth) / 2,
    top: (height - cardHeight) / 2,
    width: cardWidth,
    height: cardHeight,
  };
}

function stageGeometry(
  width: number,
  height: number,
  card: CardRect,
  dark: boolean,
): StageGeometry {
  return {
    width,
    height,
    left: card.left,
    top: card.top,
    right: card.left + card.width,
    bottom: card.top + card.height,
    cardWidth: card.width,
    cardHeight: card.height,
    cx: card.left + card.width / 2,
    cy: card.top + card.height / 2,
    dark,
  };
}

/** Lays the style out against `stage`, from the scene's own seed. */
function construct(
  style: CelebrationStyle,
  palette: CelebrationPaletteName,
  seed: number,
  stage: StageGeometry,
) {
  return buildConstructionScene(
    style,
    stage,
    createRng(seed),
    CELEBRATION_PALETTES[palette],
  );
}

/**
 * Builds a scene. `card` is where the notice rests; the props are laid out
 * against it.
 */
export function buildCelebrationScene({
  seed,
  style,
  palette,
  width,
  height,
  card = defaultCardRect(width, height),
  dark = false,
}: {
  seed: number;
  style: CelebrationStyle;
  palette: CelebrationPaletteName;
  width: number;
  height: number;
  card?: CardRect;
  dark?: boolean;
}): CelebrationScene {
  const stage = stageGeometry(width, height, card, dark);
  return {
    style,
    paletteName: palette,
    seed,
    width,
    height,
    stage,
    construction: construct(style, palette, seed, stage),
  };
}

/**
 * The viewport or the card moved. The scene is rebuilt from its seed against
 * the new layout, so its props stay lined up with the card.
 */
export function resizeScene(
  scene: CelebrationScene,
  {
    width,
    height,
    card = defaultCardRect(width, height),
  }: { width: number; height: number; card?: CardRect },
) {
  scene.width = width;
  scene.height = height;
  scene.stage = stageGeometry(width, height, card, scene.stage.dark);
  scene.construction = construct(
    scene.style,
    scene.paletteName,
    scene.seed,
    scene.stage,
  );
}

/** Where the notice is at `time`, as an offset from rest. */
export function celebrationCardPose(
  scene: CelebrationScene,
  time: number,
): CardPose {
  return constructionCardPose(scene.construction, scene.stage, time);
}

/**
 * Whether the notice is in place for good at `time`: from the style's settle
 * time. A pose that passes through rest mid-bounce is not settled.
 */
export function isCelebrationCardSettled(
  scene: CelebrationScene,
  time: number,
): boolean {
  return time >= Math.min(scene.construction.settledAt, CELEBRATION_DURATION_S);
}

/** Clears the canvas and draws the scene at `time` seconds. */
export function paintScene(
  ctx: CanvasRenderingContext2D,
  scene: CelebrationScene,
  time: number,
) {
  ctx.clearRect(0, 0, scene.width, scene.height);
  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
  ctx.save();
  scene.construction.draw(ctx, time);
  ctx.restore();
}
